import { FC } from "react";

import { Stack, Box, useMultiStyleConfig } from "@chakra-ui/react";

import OptionButton from "./OptionButton";
import { TOptionButtonGroupRowProps, TOptionButtonGroupMultipleValues, TOptionButtonValue } from "./types";

const getKey = (index: number, innerValue: TOptionButtonValue) => `option-group-item-${innerValue}-${index}`;

const OptionButtonGroupRow: FC<TOptionButtonGroupRowProps> = ({
  options,
  value,
  name,
  testId,
  noWrap = false,
  isVertical = false,
  onChange,
  width = "7rem",
  height,
  minWidth,
  isFullWidth = false,
  isMultiple,
  isFill = false,
  ...rest
}) => {
  const styles = useMultiStyleConfig("OptionButton", {});

  const handleChange = (innerValue: TOptionButtonValue) => () => {
    if (!isMultiple) {
      onChange?.(innerValue);
      return;
    }

    const safeValue = Array.isArray(value) ? (value as TOptionButtonGroupMultipleValues) : [];

    if (safeValue?.includes(innerValue)) {
      onChange?.(safeValue.filter((valueItem) => valueItem !== innerValue));
      return;
    }
    onChange?.([...safeValue, innerValue]);
  };

  const getIsSelected = (innerValue: TOptionButtonValue): boolean =>
    (isMultiple
      ? (value as TOptionButtonGroupMultipleValues)?.includes(innerValue)
      : innerValue === (value as TOptionButtonValue)) || false;

  return (
    <Stack
      direction={isVertical ? "column" : "row"}
      width="auto"
      wrap={!noWrap && !isVertical && !isFullWidth ? "wrap" : "nowrap"}
      sx={styles.row}
      spacing="0"
      {...(isFill && {
        minW: "36.5rem",
        justifyContent: "space-between",
      })}
    >
      {options.map(
        (
          {
            label,
            value: innerValue,
            isDisabled = false,
            width: optionWidth,
            height: optionHeight,
            minWidth: optionMinWidth,
            rightIcon,
            leftIcon,
            flexBasis,
            sx,
          },
          index
        ) => (
          <Box
            sx={{
              ...styles.rowItem,
              ...(isFullWidth && !flexBasis && { flex: 1, width: "0" }),
              ...(isFill && { flexGrow: 1, width: "auto" }),
            }}
            key={getKey(index, innerValue)}
            flexBasis={flexBasis}
          >
            <OptionButton
              name={name && `${name}-${innerValue}`}
              id={name && `${name}-${innerValue}`}
              testId={testId}
              isSelected={getIsSelected(innerValue)}
              isDisabled={isDisabled}
              onClick={handleChange(innerValue)}
              width={isFill || isVertical || isFullWidth ? "100%" : optionWidth || width}
              minWidth={optionMinWidth || minWidth}
              height={optionHeight || height}
              isFullWidth={isFullWidth}
              rightIcon={rightIcon}
              leftIcon={leftIcon}
              sx={{
                ...sx,
                ...rest.sx,
              }}
              shouldWrap
            >
              {label}
            </OptionButton>
          </Box>
        )
      )}
    </Stack>
  );
};

export default OptionButtonGroupRow;
